// Mercapoli - Admin Cupones
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Tag, Plus, Loader2, Trash2, Edit, 
  ToggleLeft, ToggleRight, Percent, DollarSign, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cupones as cuponesApi } from '../../lib/api';
import { toast } from 'sonner';

const AdminCupones = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cupones, setCupones] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCupon, setEditingCupon] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    codigo: '',
    tipo: 'porcentaje',
    valor: '',
    descripcion: '',
    fecha_inicio: '',
    fecha_fin: '',
    usos_maximos: '',
    monto_minimo: '0',
    solo_primer_pedido: false,
    activo: true
  });

  useEffect(() => {
    loadCupones();
  }, []);

  const loadCupones = async () => {
    try {
      const data = await cuponesApi.getAll();
      setCupones(data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      codigo: '',
      tipo: 'porcentaje',
      valor: '',
      descripcion: '',
      fecha_inicio: '',
      fecha_fin: '',
      usos_maximos: '',
      monto_minimo: '0',
      solo_primer_pedido: false,
      activo: true
    });
    setEditingCupon(null);
  };

  const openEditModal = (cupon) => {
    setEditingCupon(cupon);
    setFormData({
      codigo: cupon.codigo,
      tipo: cupon.tipo,
      valor: cupon.valor.toString(),
      descripcion: cupon.descripcion || '',
      fecha_inicio: cupon.fecha_inicio || '',
      fecha_fin: cupon.fecha_fin || '',
      usos_maximos: cupon.usos_maximos?.toString() || '',
      monto_minimo: cupon.monto_minimo?.toString() || '0',
      solo_primer_pedido: cupon.solo_primer_pedido || false,
      activo: cupon.activo
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.codigo.trim() || !formData.valor) {
      toast.error('Completa los campos requeridos');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        codigo: formData.codigo.toUpperCase(),
        tipo: formData.tipo,
        valor: parseFloat(formData.valor),
        descripcion: formData.descripcion || null,
        fecha_inicio: formData.fecha_inicio || null,
        fecha_fin: formData.fecha_fin || null,
        usos_maximos: formData.usos_maximos ? parseInt(formData.usos_maximos) : null,
        monto_minimo: parseFloat(formData.monto_minimo) || 0,
        solo_primer_pedido: formData.solo_primer_pedido,
        activo: formData.activo
      };

      if (editingCupon) {
        await cuponesApi.update(editingCupon.cupon_id, payload);
        toast.success('Cupón actualizado');
      } else {
        await cuponesApi.create(payload);
        toast.success('Cupón creado exitosamente');
      }
      
      setShowModal(false);
      resetForm();
      loadCupones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cupón');
    }
    setFormLoading(false);
  };

  const toggleActivo = async (cupon) => {
    try {
      await cuponesApi.update(cupon.cupon_id, { activo: !cupon.activo });
      toast.success(`Cupón ${!cupon.activo ? 'activado' : 'desactivado'}`);
      loadCupones();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const deleteCupon = async (cuponId) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    
    try {
      await cuponesApi.delete(cuponId);
      toast.success('Cupón eliminado');
      loadCupones();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Cupones Promocionales</h1>
            </div>
            <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-spicy-coral hover:bg-spicy-coral/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo cupón
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingCupon ? 'Editar cupón' : 'Crear nuevo cupón'}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="codigo">Código *</Label>
                      <Input
                        id="codigo"
                        placeholder="Ej: NUEVO20"
                        value={formData.codigo}
                        onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="tipo">Tipo de descuento</Label>
                      <Select 
                        value={formData.tipo} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, tipo: value }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
                          <SelectItem value="fijo">Monto fijo ($)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="valor">
                        Valor {formData.tipo === 'porcentaje' ? '(%)' : '($)'} *
                      </Label>
                      <Input
                        id="valor"
                        type="number"
                        min="0"
                        step={formData.tipo === 'porcentaje' ? '1' : '0.01'}
                        placeholder={formData.tipo === 'porcentaje' ? '20' : '50'}
                        value={formData.valor}
                        onChange={(e) => setFormData(prev => ({ ...prev, valor: e.target.value }))}
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label htmlFor="descripcion">Descripción</Label>
                      <Input
                        id="descripcion"
                        placeholder="Ej: Descuento para nuevos usuarios"
                        value={formData.descripcion}
                        onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="fecha_inicio">Fecha inicio</Label>
                      <Input
                        id="fecha_inicio"
                        type="datetime-local"
                        value={formData.fecha_inicio}
                        onChange={(e) => setFormData(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="fecha_fin">Fecha fin</Label>
                      <Input
                        id="fecha_fin"
                        type="datetime-local"
                        value={formData.fecha_fin}
                        onChange={(e) => setFormData(prev => ({ ...prev, fecha_fin: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="usos_maximos">Usos máximos</Label>
                      <Input
                        id="usos_maximos"
                        type="number"
                        min="1"
                        placeholder="Sin límite"
                        value={formData.usos_maximos}
                        onChange={(e) => setFormData(prev => ({ ...prev, usos_maximos: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="monto_minimo">Monto mínimo ($)</Label>
                      <Input
                        id="monto_minimo"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        value={formData.monto_minimo}
                        onChange={(e) => setFormData(prev => ({ ...prev, monto_minimo: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="col-span-2 flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">Solo primer pedido</p>
                        <p className="text-sm text-muted-foreground">
                          Válido solo para nuevos clientes
                        </p>
                      </div>
                      <Switch
                        checked={formData.solo_primer_pedido}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, solo_primer_pedido: checked }))}
                      />
                    </div>
                    
                    <div className="col-span-2 flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">Activo</p>
                        <p className="text-sm text-muted-foreground">
                          El cupón puede ser utilizado
                        </p>
                      </div>
                      <Switch
                        checked={formData.activo}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, activo: checked }))}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowModal(false); resetForm(); }}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 bg-spicy-coral hover:bg-spicy-coral/90"
                    >
                      {formLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        editingCupon ? 'Guardar cambios' : 'Crear cupón'
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <Tag className="h-6 w-6 mx-auto mb-2 text-spicy-coral" />
              <p className="font-heading text-2xl font-bold">{cupones.length}</p>
              <p className="text-xs text-muted-foreground">Total cupones</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <ToggleRight className="h-6 w-6 mx-auto mb-2 text-zesty-lime" />
              <p className="font-heading text-2xl font-bold">
                {cupones.filter(c => c.activo).length}
              </p>
              <p className="text-xs text-muted-foreground">Activos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="h-6 w-6 mx-auto mb-2 text-blue-500" />
              <p className="font-heading text-2xl font-bold">
                {cupones.reduce((sum, c) => sum + (c.usos_actuales || 0), 0)}
              </p>
              <p className="text-xs text-muted-foreground">Usos totales</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
          </div>
        ) : cupones.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No hay cupones creados</p>
            <Button onClick={() => setShowModal(true)} className="bg-spicy-coral hover:bg-spicy-coral/90">
              <Plus className="h-4 w-4 mr-2" />
              Crear primer cupón
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {cupones.map((cupon, index) => (
              <motion.div
                key={cupon.cupon_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={!cupon.activo ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-heading font-bold text-lg">{cupon.codigo}</p>
                          {cupon.tipo === 'porcentaje' ? (
                            <Badge className="bg-purple-100 text-purple-800">
                              <Percent className="h-3 w-3 mr-1" />
                              {cupon.valor}%
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">
                              <DollarSign className="h-3 w-3 mr-1" />
                              ${cupon.valor}
                            </Badge>
                          )}
                        </div>
                        {cupon.descripcion && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {cupon.descripcion}
                          </p>
                        )}
                      </div>
                      <Badge className={cupon.activo ? 'bg-zesty-lime/20 text-zesty-lime' : 'bg-muted text-muted-foreground'}>
                        {cupon.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-4">
                      <div>
                        <p className="text-muted-foreground">Usos</p>
                        <p className="font-medium">
                          {cupon.usos_actuales || 0}
                          {cupon.usos_maximos && ` / ${cupon.usos_maximos}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Monto mín.</p>
                        <p className="font-medium">${cupon.monto_minimo || 0}</p>
                      </div>
                      {cupon.fecha_fin && (
                        <div>
                          <p className="text-muted-foreground">Expira</p>
                          <p className="font-medium">
                            {new Date(cupon.fecha_fin).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      )}
                      {cupon.solo_primer_pedido && (
                        <div>
                          <Badge variant="outline" className="text-xs">
                            Primer pedido
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <button
                        onClick={() => toggleActivo(cupon)}
                        className={`p-2 rounded-lg transition-colors ${
                          cupon.activo 
                            ? 'text-zesty-lime hover:bg-zesty-lime/10' 
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {cupon.activo ? (
                          <ToggleRight className="h-5 w-5" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(cupon)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => deleteCupon(cupon.cupon_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCupones;
